import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Check, Video, Package, Code2, Eye, Search, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { sanitizeHtml } from "@/lib/sanitize";
import { createPost, updatePost, searchProducts, fetchPublicDealer } from "@/lib/api";
import AiHelperDialog from "@/components/AiHelperDialog";
import { VideoPickerGrid } from "@/components/posts/VideoShowcaseDialog";

const esc = (s) => String(s || "").replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c]));

export function videoBlockHtml(v) {
  return `\n<div style="margin:18px 0;border:1px solid #e5e7eb;border-radius:16px;overflow:hidden;background:#000;">
  <video src="${esc(v.video_url)}" poster="${esc(v.thumbnail_url)}" controls playsinline preload="none" style="width:100%;max-height:560px;display:block;background:#000;"></video>
  <div style="padding:10px 14px;background:#fff;font-size:13px;color:#475569;"><strong style="color:#0f172a;">${esc(v.template_name)}</strong> · ${esc(v.business_name || "OreVital")}</div>
</div>\n`;
}
const FONT = "font-family:system-ui,-apple-system,Segoe UI,Roboto,Arial,sans-serif;";
export const HEADER_PRESETS = {
  hero: {
    label: "Hero image",
    html: ({ title, image }) => `\n<div style="position:relative;border-radius:20px;overflow:hidden;margin:0 0 22px;background:#0b2545;${FONT}">
  ${image ? `<img src="${esc(image)}" alt="" style="width:100%;height:320px;object-fit:cover;display:block;opacity:.85;" />` : `<div style="height:220px;background:linear-gradient(135deg,#0b2545,#1e3a8a);"></div>`}
  <div style="position:absolute;left:0;right:0;bottom:0;padding:22px 24px;background:linear-gradient(180deg,rgba(11,37,69,0),rgba(11,37,69,.92));color:#fff;">
    <div style="font-size:12px;letter-spacing:.3px;opacity:.85;">🧬 OreVital • Professional-Only Peptide Innovation</div>
    <h2 style="margin:6px 0 0;font-size:30px;line-height:1.15;">${esc(title || "Your headline here")}</h2>
  </div>
</div>\n`,
  },
  quote: {
    label: "Quote",
    html: ({ who }) => `\n<blockquote style="margin:0 0 22px;padding:26px 28px;border-left:5px solid #0b2545;border-radius:0 20px 20px 0;background:#f8fafc;${FONT}">
  <p style="margin:0;font-size:22px;line-height:1.4;color:#0f172a;font-style:italic;">“Replace this with the quote you want visitors to remember — a client result, a mission statement, a promise.”</p>
  <footer style="margin-top:12px;font-size:13px;font-weight:700;color:#475569;">— ${esc(who || "Your name, OreVital Pro Dealer")}</footer>
</blockquote>\n`,
  },
  intro: {
    label: "Dealer intro",
    html: ({ who, business, city, photo, logo }) => `\n<div style="display:flex;gap:18px;align-items:center;margin:0 0 22px;padding:18px;border:1px solid #e5e7eb;border-radius:20px;background:#fff;${FONT}">
  ${photo || logo ? `<img src="${esc(photo || logo)}" alt="${esc(who)}" style="width:96px;height:96px;border-radius:50%;object-fit:cover;flex:none;border:3px solid #f1f5f9;" />` : ""}
  <div style="min-width:0;">
    <div style="font-size:12px;font-weight:700;letter-spacing:.3px;color:#0b2545;text-transform:uppercase;">Meet your OreVital Pro Dealer</div>
    <h2 style="margin:4px 0 2px;font-size:26px;line-height:1.2;color:#0f172a;">${esc(who || "Your name")}</h2>
    <div style="font-size:14px;color:#475569;">${esc([business, city].filter(Boolean).join(" · ") || "Your business · Your city")}</div>
    <p style="margin:10px 0 0;font-size:14px;line-height:1.6;color:#334155;">Write two or three lines about who you help, what you specialise in and why clients love working with you.</p>
  </div>
</div>\n`,
  },
};

/** Put header HTML right after the opening <section …> tag (or at the top when there is none). */
export function prependHeader(html, header) {
  const m = /<section\b[^>]*>/i.exec(html || "");
  if (!m) return header + (html || "");
  const at = m.index + m[0].length;
  return html.slice(0, at) + header + html.slice(at);
}

export function productCardHtml(p) {
  const price = p.price ? `$${Number(p.price).toFixed(2)}` : "";
  const key = esc(p.slug || p.id);
  // Only <span>/<img> inside the <a>: block children (div/strong) get split out of the link by HTML
  // normalisers (editor / AI / Airtable), which is what used to break these cards.
  return `\n<div data-product-card="${key}" style="margin:14px 0;">` +
    `<a href="/d/orevital/product/${encodeURIComponent(p.slug || p.id)}" data-product="${key}" style="display:flex;gap:14px;align-items:center;padding:12px;border:1px solid #e5e7eb;border-radius:16px;background:#fff;text-decoration:none;color:#0f172a;">` +
    (p.image_url ? `<img src="${esc(p.image_url)}" alt="${esc(p.name)}" style="width:84px;height:84px;object-fit:cover;border-radius:12px;flex:none;" />` : "") +
    `<span style="display:block;min-width:0;">` +
    `<span style="display:block;font-size:15px;font-weight:700;">${esc(p.name)}</span>` +
    `<span style="display:block;font-size:13px;color:#475569;line-height:1.5;margin-top:3px;">${esc((p.excerpt || "").slice(0, 140))}</span>` +
    `<span style="display:block;margin-top:6px;font-size:13px;font-weight:700;color:#0b2545;">${price}${price ? " · " : ""}Shop now →</span>` +
    `</span></a></div>\n`;
}

// Products referenced by cards in the HTML → [{key, name}]
export function productsInHtml(html) {
  const out = [];
  const seen = new Set();
  const re = /<a[^>]+data-product="([^"]+)"[^>]*>([\s\S]*?)<\/a>/gi;
  let m;
  while ((m = re.exec(html || ""))) {
    if (seen.has(m[1])) continue;
    seen.add(m[1]);
    const alt = /alt="([^"]*)"/i.exec(m[2]);
    const name = /font-weight:\s*700[^>]*>([^<]+)</i.exec(m[2]);
    out.push({ key: m[1], name: (name?.[1] || alt?.[1] || m[1]).replace(/&amp;/g, "&").replace(/&trade;/g, "™") });
  }
  return out;
}

export function removeProductCard(html, key) {
  const k = key.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  return (html || "")
    .replace(new RegExp(`\\s*<div data-product-card="${k}"[^>]*>[\\s\\S]*?<\\/a>\\s*<\\/div>`, "gi"), "")
    .replace(new RegExp(`\\s*<a[^>]+data-product="${k}"[^>]*>[\\s\\S]*?<\\/a>`, "gi"), "");
}

function ProductPicker({ userRecordId, onPick }) {
  const [q, setQ] = useState("");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    let dead = false;
    setLoading(true);
    searchProducts(q, 30, { callerUserRecordId: userRecordId }).then((r) => { if (!dead) setItems(r.items || r.products || []); }).catch(() => {}).finally(() => !dead && setLoading(false));
    return () => { dead = true; };
  }, [q, userRecordId]);
  return (
    <div className="space-y-2" data-testid="product-picker">
      <div className="relative">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search the catalogue…" className="pl-9 h-9 rounded-xl" data-testid="product-picker-search" />
      </div>
      <div className="max-h-[40vh] overflow-y-auto divide-y rounded-xl border">
        {loading && !items.length ? <div className="p-4 text-sm text-zinc-500 text-center"><Loader2 className="inline animate-spin" size={14} /></div> : null}
        {items.map((p) => (
          <button key={p.id} type="button" onClick={() => onPick(p)} className="w-full flex items-center gap-3 p-2 text-left hover:bg-zinc-50" data-testid={`product-pick-${p.slug}`}>
            {p.image_url ? <img src={p.image_url} alt="" className="h-10 w-10 rounded-lg object-cover" /> : <div className="h-10 w-10 rounded-lg bg-zinc-100" />}
            <div className="min-w-0 flex-1"><div className="text-sm font-semibold truncate">{p.name}</div><div className="text-xs text-zinc-500">{p.price ? `$${p.price}` : ""}</div></div>
          </button>
        ))}
      </div>
    </div>
  );
}

// Create / edit a regular post: title, media on top (URL or library video), HTML (AI improve, insert video/product), sharing.
export default function PostEditorDialog({ userRecordId, isAdmin, post, onClose, onSaved }) {
  const [title, setTitle] = useState(post?.title || "");
  const [caption, setCaption] = useState(post?.caption || "");
  const [videoUrl, setVideoUrl] = useState(post?.video_url || "");
  const [imageUrl, setImageUrl] = useState(post?.image_url || "");
  const [html, setHtml] = useState(post?.html || "");
  const [shared, setShared] = useState(post ? !!post.shared : false);
  const [preview, setPreview] = useState(false);
  const [insert, setInsert] = useState(null); // "video" | "product" | "topvideo"
  const [headerBusy, setHeaderBusy] = useState("");
  // Videos already in this post: the showcase grid ids + any <video src> pasted into the HTML.
  const inPost = [...(post?.video_ids || []), ...Array.from(html.matchAll(/<video[^>]*\ssrc="([^"]+)"/gi), (m) => m[1])];

  async function addHeader(key) {
    setHeaderBusy(key);
    try {
      let ctx = { title, image: imageUrl, who: "", business: "", city: "", photo: "", logo: "" };
      if (key !== "hero" || !imageUrl) {
        try {
          const d = await fetchPublicDealer(userRecordId);
          ctx = { ...ctx, who: `${d.first_name || ""} ${d.last_name || ""}`.trim(), business: d.company_name || "", city: [d.city, d.state].filter(Boolean).join(", "),
                  photo: d.image_url || "", logo: d.dealer_logo || "", image: imageUrl || d.dealer_logo || d.image_url || "" };
        } catch { /* fall back to placeholders */ }
      }
      setHtml((h) => prependHeader(h, HEADER_PRESETS[key].html(ctx)));
      setPreview(true);
      toast.success(`${HEADER_PRESETS[key].label} header added — edit the words in the HTML or ask AI to polish it`);
    } finally { setHeaderBusy(""); }
  }
  const [saving, setSaving] = useState(false);

  async function save() {
    if (!title.trim()) { toast.error("Title is required"); return; }
    if (!html.trim() && !videoUrl.trim()) { toast.error("Add some content or a video"); return; }
    setSaving(true);
    try {
      const body = { user_record_id: userRecordId, title: title.trim(), caption: caption.trim(), video_url: videoUrl.trim(), image_url: imageUrl.trim(), html, shared };
      const r = post ? await updatePost(post.id, body) : await createPost(body);
      toast.success(post ? "Post saved" : "Post created — add it to your home tabs in Settings");
      onSaved(r.post);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save post");
    } finally { setSaving(false); }
  }
  const append = (snippet) => { setHtml((h) => (h || "") + snippet); setInsert(null); setPreview(true); };

  return (
    <section className="rounded-2xl border border-violet-200 bg-white p-4 space-y-3" data-testid="post-editor-dialog">
      <div className="flex items-center justify-between">
        <h3 className="text-base font-semibold text-zinc-900">{post ? "Edit post" : "New post"}</h3>
        <button type="button" onClick={onClose} className="text-xs font-semibold text-zinc-500 hover:text-zinc-800" data-testid="post-editor-dialog-back">← Back to posts</button>
      </div>
      <div>
        <div className="space-y-3">
          <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Post title (becomes the tab name)" className="rounded-xl" data-testid="post-title-input" />
          <Textarea value={caption} onChange={(e) => setCaption(e.target.value)} rows={2} placeholder="Short caption / summary (used by AI and the blog list)" className="rounded-xl text-sm" data-testid="post-caption-input" />

          <div className="rounded-xl border border-zinc-200 p-3 space-y-2">
            <div className="text-xs font-semibold text-zinc-700 flex items-center gap-1.5"><Video size={13} /> Video on top of the post</div>
            <div className="flex gap-2">
              <Input value={videoUrl} onChange={(e) => setVideoUrl(e.target.value)} placeholder="YouTube, Instagram reel or video URL" className="rounded-xl h-9 text-sm" data-testid="post-video-url" />
              <Button type="button" variant="outline" onClick={() => setInsert(insert === "topvideo" ? null : "topvideo")} className="h-9 rounded-xl text-xs shrink-0" data-testid="post-pick-top-video">From library</Button>
            </div>
            {insert === "topvideo" && <VideoPickerGrid userRecordId={userRecordId} selected={[]} inPost={inPost} single onToggle={(v) => { setVideoUrl(v.video_url); if (!imageUrl) setImageUrl(v.thumbnail_url || ""); setInsert(null); }} />}
          </div>

          <div className="rounded-xl border border-zinc-200 p-3 space-y-2">
            {post?.video_ids?.length ? (
              <p className="text-[11px] text-amber-800 bg-amber-50 rounded-lg px-2.5 py-1.5" data-testid="post-showcase-hint">
                This post has a video grid ({post.video_ids.length} video{post.video_ids.length === 1 ? "" : "s"}). Design the header and text freely — the grid between the <code>&lt;!--video-grid--&gt;</code> markers is kept when you change videos from the list.
              </p>
            ) : null}
            <div className="flex flex-wrap items-center gap-2">
              <div className="text-xs font-semibold text-zinc-700 flex items-center gap-1.5 mr-auto"><Code2 size={13} /> Post content (HTML)</div>
              <AiHelperDialog userRecordId={userRecordId} kind="post" existing={html} label={html ? "Improve with AI" : "Write with AI"} onResult={(v) => { setHtml(v); setPreview(true); }} />
              <Button type="button" variant="outline" size="sm" onClick={() => setInsert(insert === "video" ? null : "video")} className="h-8 rounded-lg text-xs" data-testid="post-insert-video"><Video size={12} className="mr-1" /> Insert video</Button>
              <Button type="button" variant="outline" size="sm" onClick={() => setInsert(insert === "product" ? null : "product")} className="h-8 rounded-lg text-xs" data-testid="post-insert-product"><Package size={12} className="mr-1" /> Insert product</Button>
              <Button type="button" variant={preview ? "default" : "outline"} size="sm" onClick={() => setPreview((v) => !v)} className="h-8 rounded-lg text-xs" data-testid="post-preview-toggle"><Eye size={12} className="mr-1" /> {preview ? "Edit HTML" : "Preview"}</Button>
            </div>
            <div className="flex flex-wrap items-center gap-1.5 text-xs" data-testid="post-header-presets">
              <span className="font-semibold text-zinc-600 mr-1">Add header:</span>
              {Object.entries(HEADER_PRESETS).map(([k, v]) => (
                <button key={k} type="button" onClick={() => addHeader(k)} disabled={!!headerBusy} data-testid={`post-header-${k}`}
                  className="h-7 px-2.5 rounded-full border border-zinc-200 bg-white hover:bg-violet-50 hover:border-violet-300 font-semibold text-zinc-700">
                  {headerBusy === k ? <Loader2 className="inline animate-spin" size={11} /> : v.label}
                </button>
              ))}
            </div>
            {insert === "video" && <VideoPickerGrid userRecordId={userRecordId} selected={[]} inPost={inPost} single onToggle={(v) => { if (inPost.includes(v.post_rec_id) || inPost.includes(v.video_url)) { if (!window.confirm("This video is already in the post. Insert it again?")) return; } append(videoBlockHtml(v)); toast.success(`${v.business_name || "Video"} inserted`); }} />}
            {insert === "product" && <ProductPicker userRecordId={userRecordId} onPick={(p) => append(productCardHtml(p))} />}
            {productsInHtml(html).length > 0 && (
              <div className="flex flex-wrap items-center gap-1.5 text-xs" data-testid="post-products-in-html">
                <span className="font-semibold text-zinc-600 mr-1 inline-flex items-center gap-1"><Package size={12} /> Products in this post:</span>
                {productsInHtml(html).map((pr) => (
                  <span key={pr.key} className="inline-flex items-center gap-1 rounded-full bg-emerald-50 border border-emerald-100 text-emerald-800 pl-2.5 pr-1 py-0.5 font-semibold" data-testid={`post-product-chip-${pr.key}`}>
                    {pr.name}
                    <button type="button" onClick={() => setHtml((h) => removeProductCard(h, pr.key))} title="Remove this product card from the post"
                      className="h-5 w-5 rounded-full hover:bg-emerald-100 inline-flex items-center justify-center" data-testid={`post-product-remove-${pr.key}`}>
                      <X size={11} />
                    </button>
                  </span>
                ))}
              </div>
            )}
            {preview ? (
              <div className="rounded-xl border bg-white p-3 max-h-[50vh] overflow-auto prose prose-sm max-w-none" data-testid="post-preview" dangerouslySetInnerHTML={{ __html: sanitizeHtml(html) }} />
            ) : (
              <Textarea value={html} onChange={(e) => setHtml(e.target.value)} rows={12} placeholder="Paste or write HTML — or tap Write with AI" className="rounded-xl font-mono text-xs" data-testid="post-html-input" />
            )}
            <div className="text-[11px] text-zinc-400">{html.length} characters</div>
          </div>

          <div className="flex flex-wrap items-center justify-between gap-2">
            {isAdmin ? (
              <span className="text-xs text-zinc-500">OreVital posts are visible to every dealer.</span>
            ) : (
              <label className="flex items-center gap-2 text-xs text-zinc-700">
                <input type="checkbox" checked={shared} onChange={(e) => setShared(e.target.checked)} data-testid="post-shared" /> Share with other dealers (otherwise private to you)
              </label>
            )}
            <div className="flex gap-2">
              <Button variant="ghost" onClick={onClose} className="rounded-xl">Cancel</Button>
              <Button onClick={save} disabled={saving} className="rounded-xl bg-violet-600 hover:bg-violet-700 text-white" data-testid="post-save">
                {saving ? <Loader2 className="animate-spin mr-1.5" size={14} /> : <Check size={14} className="mr-1.5" />} {post ? "Save post" : "Create post"}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
